import { Button } from '@calizahq/react-components-v2'
import { WorkspaceTab, WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { BookableDuplicatesMutations } from '@sales-app/graphql/mutations/bookableDuplicates.mutations'
import { BookablePersonDuplicate } from '@sales-app/types/BookablePersonDuplicate'
import { BookablePersonDuplicateDecision } from '@sales-app/types/BookablePersonDuplicateDecision'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import React, { FC, useState } from 'react'

import { useAlertContext } from '@/context'
import { useGraphqlMutation } from '@/hooks/useGraphql'

type Props = {
  bookablePerson: WorkspaceTab
  closeModal: (path?: string) => void
  duplicateReview: BookablePersonDuplicate
  refetch: () => void
}
enum Option {
  New = 'New',
  Original = 'Original',
}

export const DuplicateReview: FC<Props> = ({ duplicateReview, bookablePerson, refetch, closeModal }) => {
  const [selectedName, setSelectedName] = useState<Option>()
  const [selectedEmail, setSelectedEmail] = useState<Option>()
  const [selectedPhone, setSelectedPhone] = useState<Option>()
  const { setAlert } = useAlertContext()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    BookableDuplicatesMutations.updateBookablePersonDuplicateDecision(),
    {
      onSuccess: (res: {
        updateBookablePersonDuplicateDecision: {
          bookablePersonId: number
          bookablePersonType: BookablePersonType
        }
      }) => {
        const { bookablePersonId, bookablePersonType } = res.updateBookablePersonDuplicateDecision
        if (bookablePersonId && bookablePersonType) {
          setAlert('Action successfully completed', 'success')
          if (
            bookablePerson.bookablePersonId === bookablePersonId &&
            bookablePerson.bookablePersonType === bookablePersonType
          ) {
            refetch()
            closeModal()
          } else {
            refetch()
            const path = getWorkspacePath({
              view: WorkspaceView.SUMMARY,
              type: bookablePersonType,
              id: bookablePersonId,
              potentialBookingId: null,
            })
            closeModal(path)
          }
        } else {
          closeModal()
        }
      },
    },
  )

  const handlePhoneSelection = (val: Option) => {
    if (selectedPhone === val) {
      setSelectedPhone(undefined)
    } else {
      setSelectedPhone(val)
    }
  }

  const handleEmailSelection = (val: Option) => {
    if (selectedEmail === val) {
      setSelectedEmail(undefined)
    } else {
      setSelectedEmail(val)
    }
  }

  const handleNameSelection = (val: Option) => {
    if (selectedName === val) {
      setSelectedName(undefined)
    } else {
      setSelectedName(val)
    }
  }

  const handleDonotMerge = () => {
    mutate({
      bookablePersonDuplicateDecision: {
        bookablePersonDuplicateDecisionId: duplicateReview.id,
        decision: BookablePersonDuplicateDecision.Mismatch,
      },
    })
  }

  const handleMerge = () => {
    mutate({
      bookablePersonDuplicateDecision: {
        bookablePersonDuplicateDecisionId: duplicateReview.id,
        decision: BookablePersonDuplicateDecision.Match,
        nameToUse: selectedName,
        emailAddressToUse: selectedEmail,
        phoneNumberToUse: selectedPhone,
      },
    })
  }

  const isMergedEnabled = !!selectedEmail && !!selectedName && !!selectedPhone && !isMutationLoading
  return (
    <div className="flex w-full max-w-195 flex-col flex-wrap px-6">
      <h2 className=" mb-2 text-header-md">Potential duplicate users review</h2>
      <p className=" mb-2 text-p-sm">
        Please review the potential duplicate users&rsquo; details. By confirming, the details including potential
        booking information will be merged into one user. View other lead&rsquo;s details
      </p>
      <div className="flex w-full flex-col">
        <div className="mb-4 grid grid-cols-2 gap-8">
          <Card
            active={selectedName === Option.New}
            bookablePersonType={duplicateReview.newBookablePersonType}
            onClick={() => handleNameSelection(Option.New)}
            type="name"
            value={duplicateReview.originalName}
          />
          <Card
            active={selectedName === Option.Original}
            bookablePersonType={duplicateReview.originalBookablePersonType}
            onClick={() => handleNameSelection(Option.Original)}
            type="name"
            value={duplicateReview.originalName}
          />
        </div>
        <div className="mb-4 grid grid-cols-2 gap-8">
          <Card
            active={selectedEmail === Option.New}
            bookablePersonType={duplicateReview.newBookablePersonType}
            onClick={() => handleEmailSelection(Option.New)}
            type="email"
            value={duplicateReview.newEmail}
          />
          <Card
            active={selectedEmail === Option.Original}
            bookablePersonType={duplicateReview.originalBookablePersonType}
            onClick={() => handleEmailSelection(Option.Original)}
            type="email"
            value={duplicateReview.originalEmail}
          />
        </div>
        <div className="mb-4 grid grid-cols-2 gap-8">
          <Card
            active={selectedPhone === Option.New}
            bookablePersonType={duplicateReview.newBookablePersonType}
            onClick={() => handlePhoneSelection(Option.New)}
            type="phone number"
            value={duplicateReview.newPhoneNumber}
          />
          <Card
            active={selectedPhone === Option.Original}
            bookablePersonType={duplicateReview.originalBookablePersonType}
            onClick={() => handlePhoneSelection(Option.Original)}
            type="phone number"
            value={duplicateReview.originalPhoneNumber}
          />
        </div>
      </div>
      <div className="flex justify-between border-t pt-4 border-secondary">
        <Button
          className="bg-action-secondary"
          disabled={isMutationLoading}
          onClick={handleDonotMerge}
          size="small"
          variant="destructive"
        >
          Do not merge
        </Button>
        <Button className=" bg-action-primary" disabled={!isMergedEnabled} onClick={handleMerge} size="small">
          Merge
        </Button>
      </div>
    </div>
  )
}

type CardProps = {
  active: boolean
  bookablePersonType: BookablePersonType
  onClick: () => void
  type: string
  value: string | null
}

const Card: FC<CardProps> = ({ bookablePersonType, onClick, value, type, active }) => {
  return (
    <div
      className={`flex w-full cursor-pointer flex-col rounded-lg border-2 p-4 ${
        active ? ' border-action-primary' : '  border-secondary'
      }`}
      onClick={onClick}
      role="presentation"
    >
      <p>
        Use this {bookablePersonType} {type}
      </p>
      <p className="text-p-sm" style={{ overflowWrap: 'break-word' }}>
        {value}
      </p>
    </div>
  )
}
